import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../utils/cn';
import KoobLogo, { KoobGlyph } from './KoobLogo';

/**
 * Every sign-in screen (login, password reset, sign-up, MCP consent): the
 * brand on a navy panel at the left, the form at the right. Phones get the
 * form alone, under the wordmark.
 */
export default function KoobAuthCard({
  title,
  subtitle,
  wide = false,
  children,
}: {
  title?: string;
  subtitle?: string;
  /** A long form (TO sign-up): a wider column. */
  wide?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation('auth');

  return (
    <div className="flex min-h-screen bg-white text-k-h">
      <aside className="sticky top-0 hidden h-screen w-1/2 shrink-0 flex-col justify-between overflow-hidden bg-k-h p-14 lg:flex">
        <KoobLogo reverse className="relative h-[42px] w-auto self-start" />
        <KoobGlyph
          top="fill-navy-700"
          className="pointer-events-none absolute -bottom-[9vh] right-0 h-[78vh] w-auto translate-x-1/4"
        />
        <div className="relative flex max-w-[420px] flex-col gap-3">
          <p className="m-0 text-[32px] font-bold leading-10 text-white">
            {t('tagline')}
          </p>
          <p className="m-0 text-[15px] leading-6 text-navy-200">
            {t('taglineDetail')}
          </p>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-6 py-14">
        <div className={cn('w-full', wide ? 'max-w-2xl' : 'max-w-[400px]')}>
          <KoobLogo className="mb-10 h-8 w-auto lg:hidden" />
          {title && (
            <h1 className="m-0 text-[28px] font-semibold leading-9">{title}</h1>
          )}
          {subtitle && <p className="m-0 mt-2 text-sm text-k-p">{subtitle}</p>}
          <div className={cn((title || subtitle) && 'mt-5')}>{children}</div>
        </div>
      </main>
    </div>
  );
}
